import React from 'react'
import { isMotionType } from '../utils/motionEngine'

/**
 * Motion Properties Panel — editable fields for the Text Motion applied to a
 * text item (item.motion). Mirrors AnimationPropertiesPanel but writes to the
 * single `motion` config object. Shows common timing + easing, type-specific
 * fields, and the universal loop / speed / constraint modifiers that the motion
 * engine core applies on top of any preset.
 */

const MOTION_TYPE_LABELS = {
  positionMotion: 'Position', scaleMotion: 'Scale', rotationMotion: 'Rotation',
  opacityMotion: 'Opacity', anchorPointMotion: 'Anchor Point',
  letterSpacingMotion: 'Letter Spacing', lineSpacingMotion: 'Line Spacing',
  directionalTravel: 'Directional Travel', diagonalMotion: 'Diagonal',
  zoomTravel: 'Zoom Travel', orbitMotion: 'Orbit', circularMotion: 'Circular',
  followPath: 'Follow Path', motionPathWithStops: 'Path With Stops',
  overshootSettle: 'Overshoot & Settle', elasticMotion: 'Elastic',
  staggeredCharMotion: 'Staggered Chars', staggeredWordMotion: 'Staggered Words',
  waveMotion: 'Wave', scatterConverge: 'Scatter & Converge',
  trackingExpandCompress: 'Tracking Expand',
  rotation3D: '3D Rotation', perspectiveTilt: 'Perspective Tilt',
  depthTravel: 'Depth Travel', parallaxMotion: 'Parallax', orbit3D: '3D Orbit',
  flipTumble3D: 'Flip & Tumble',
  textOnPath: 'Text On Path', pathMorph: 'Path Morph', spiralMotion: 'Spiral',
  wavePathMotion: 'Wave Path', layoutReflow: 'Layout Reflow',
  floatingDrifting: 'Floating Drift', springMotion: 'Spring',
  pendulumSwing: 'Pendulum', jitterTremble: 'Jitter & Tremble',
  impulseMotion: 'Impulse', rhythmicMotion: 'Rhythmic',
  keyframeInterpolation: 'Keyframes', speedGraph: 'Speed Graph',
  motionBlur: 'Motion Blur', parentLinked: 'Parent Linked',
  loopControls: 'Loop Controls', constraints: 'Constraints',
  perCharacterControls: 'Per-Character', presets: 'Presets',
}

const EASING_OPTIONS = [
  { value: 'linear', label: 'Linear' },
  { value: 'easeIn', label: 'Ease In' },
  { value: 'easeOut', label: 'Ease Out' },
  { value: 'easeInOut', label: 'Ease In-Out' },
  { value: 'spring', label: 'Spring' },
]

const LOOP_MODES = [
  { value: 'none', label: 'None' },
  { value: 'loop', label: 'Loop' },
  { value: 'pingpong', label: 'Ping-Pong' },
]

const MotionPropertiesPanel = ({ element, selectedElement, updateElementProperty }) => {
  const motion = element.motion
  if (!motion || !motion.type || !isMotionType(motion.type)) return null

  const type = motion.type

  const update = (key, value) => {
    updateElementProperty(selectedElement, 'motion', { ...motion, [key]: value })
  }
  // Update a nested object field (e.g. loop.mode, speed.graph, constrain.maxX).
  const updateNested = (parentKey, childKey, value) => {
    const parent = motion[parentKey] || {}
    updateElementProperty(selectedElement, 'motion', {
      ...motion, [parentKey]: { ...parent, [childKey]: value },
    })
  }

  const removeMotion = () => {
    updateElementProperty(selectedElement, 'motion', undefined)
  }

  return (
    <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-200">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-indigo-700">
          Motion: {MOTION_TYPE_LABELS[type] || type}
        </h4>
        <button
          onClick={removeMotion}
          className="px-2 py-0.5 text-xs bg-red-100 text-red-600 hover:bg-red-200 rounded transition-colors"
        >
          Remove
        </button>
      </div>

      <div className="space-y-3">
        {/* Common: Timing */}
        <div className="grid grid-cols-2 gap-2">
          <Num label="Start (s)" value={motion.start} fallback={0} min={0} step={0.1}
            onChange={(v) => update('start', Math.max(0, v || 0))} />
          <Num label="Duration (s)" value={motion.duration} fallback={2} min={0.1} step={0.1}
            onChange={(v) => update('duration', Math.max(0.1, v || 2))} />
        </div>

        {/* Common: Easing (not every preset reads it, but harmless) */}
        <Select label="Easing" value={motion.easing} fallback="easeInOut" options={EASING_OPTIONS}
          onChange={(v) => update('easing', v)} />

        {/* Type-specific fields */}
        {renderTypeFields(type, motion, update)}

        {/* Universal modifiers (engine core): loop, speed, constraints */}
        <div className="pt-2 mt-2 border-t border-indigo-200 space-y-2">
          <p className="text-xs font-medium text-indigo-600 uppercase tracking-wide">Modifiers</p>

          {/* Loop */}
          <div className="grid grid-cols-2 gap-2">
            <Select label="Loop" value={motion.loop?.mode} fallback="none" options={LOOP_MODES}
              onChange={(v) => updateNested('loop', 'mode', v === 'none' ? undefined : v)} />
            <Num label="Loop Count (0=∞)" value={motion.loop?.count} fallback={0} min={0} step={1}
              onChange={(v) => updateNested('loop', 'count', Math.max(0, Math.floor(v || 0)))} />
          </div>

          {/* Speed multiplier */}
          <Num label="Speed ×" value={motion.speed?.multiplier} fallback={1} min={0.1} max={5} step={0.1}
            onChange={(v) => updateNested('speed', 'multiplier', Math.max(0.1, v || 1))} />

          {/* Positional constraints */}
          <div className="grid grid-cols-2 gap-2">
            <Num label="Max X (px)" value={motion.constrain?.maxX} fallback={''} step={10}
              onChange={(v) => updateNested('constrain', 'maxX', isNaN(v) ? undefined : v)} />
            <Num label="Max Y (px)" value={motion.constrain?.maxY} fallback={''} step={10}
              onChange={(v) => updateNested('constrain', 'maxY', isNaN(v) ? undefined : v)} />
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shared inputs
// ---------------------------------------------------------------------------

function Num({ label, value, fallback, min, max, step, onChange }) {
  return (
    <div>
      <label className="text-xs text-gray-500 block mb-1">{label}</label>
      <input type="number" value={value ?? fallback}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
        min={min} max={max} step={step} />
    </div>
  )
}

function Select({ label, value, fallback, options, onChange }) {
  return (
    <div>
      <label className="text-xs text-gray-500 block mb-1">{label}</label>
      <select value={value || fallback}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400">
        {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
    </div>
  )
}

function Check({ label, value, fallback, onChange }) {
  const checked = value !== undefined ? value : fallback
  return (
    <label className="flex items-center gap-2 text-xs text-gray-600">
      <input type="checkbox" checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-400" />
      {label}
    </label>
  )
}

function Row({ children }) {
  return <div className="grid grid-cols-2 gap-2">{children}</div>
}

// ---------------------------------------------------------------------------
// Type-specific fields. Covers the primary parameters of each preset; the
// universal modifiers above handle loop/speed/constraints for all of them.
// ---------------------------------------------------------------------------

function renderTypeFields(type, m, update) {
  switch (type) {
    // --- Category 1 ---
    case 'positionMotion':
      return (
        <Row>
          <Num label="End X" value={m.endX} fallback={200} step={10} onChange={(v) => update('endX', v || 0)} />
          <Num label="End Y" value={m.endY} fallback={0} step={10} onChange={(v) => update('endY', v || 0)} />
        </Row>
      )
    case 'scaleMotion':
      return (
        <Row>
          <Num label="Start Scale" value={m.startScale} fallback={1} min={0} step={0.1} onChange={(v) => update('startScale', v || 0)} />
          <Num label="End Scale" value={m.endScale} fallback={1.5} min={0} step={0.1} onChange={(v) => update('endScale', v || 0)} />
        </Row>
      )
    case 'rotationMotion':
      return (
        <Row>
          <Num label="Start °" value={m.startAngle} fallback={0} step={15} onChange={(v) => update('startAngle', v || 0)} />
          <Num label="End °" value={m.endAngle} fallback={360} step={15} onChange={(v) => update('endAngle', v || 0)} />
        </Row>
      )
    case 'opacityMotion':
      return (
        <Row>
          <Num label="Start Opacity" value={m.startOpacity} fallback={1} min={0} max={1} step={0.05} onChange={(v) => update('startOpacity', v)} />
          <Num label="End Opacity" value={m.endOpacity} fallback={0} min={0} max={1} step={0.05} onChange={(v) => update('endOpacity', v)} />
        </Row>
      )
    case 'anchorPointMotion':
      return (
        <>
          <Row>
            <Num label="Start Anchor X" value={m.startAnchorX} fallback={0.5} min={0} max={1} step={0.05} onChange={(v) => update('startAnchorX', v)} />
            <Num label="Start Anchor Y" value={m.startAnchorY} fallback={0.5} min={0} max={1} step={0.05} onChange={(v) => update('startAnchorY', v)} />
          </Row>
          <Row>
            <Num label="End Anchor X" value={m.endAnchorX} fallback={0} min={0} max={1} step={0.05} onChange={(v) => update('endAnchorX', v)} />
            <Num label="End Anchor Y" value={m.endAnchorY} fallback={0} min={0} max={1} step={0.05} onChange={(v) => update('endAnchorY', v)} />
          </Row>
        </>
      )
    case 'letterSpacingMotion':
      return (
        <Row>
          <Num label="Start Tracking" value={m.startTracking} fallback={20} step={1} onChange={(v) => update('startTracking', v || 0)} />
          <Num label="End Tracking" value={m.endTracking} fallback={0} step={1} onChange={(v) => update('endTracking', v || 0)} />
        </Row>
      )
    case 'lineSpacingMotion':
      return (
        <Row>
          <Num label="Start Spacing" value={m.startLineSpacing} fallback={0} step={0.05} onChange={(v) => update('startLineSpacing', v || 0)} />
          <Num label="End Spacing" value={m.endLineSpacing} fallback={0.6} step={0.05} onChange={(v) => update('endLineSpacing', v || 0)} />
        </Row>
      )

    // --- Category 2 ---
    case 'directionalTravel':
      return (
        <Row>
          <Num label="Angle °" value={m.angle} fallback={0} step={15} onChange={(v) => update('angle', v || 0)} />
          <Num label="Distance" value={m.distance} fallback={300} step={10} onChange={(v) => update('distance', v || 0)} />
        </Row>
      )
    case 'diagonalMotion':
      return (
        <Row>
          <Num label="Distance X" value={m.distanceX} fallback={300} step={10} onChange={(v) => update('distanceX', v || 0)} />
          <Num label="Distance Y" value={m.distanceY} fallback={200} step={10} onChange={(v) => update('distanceY', v || 0)} />
        </Row>
      )
    case 'zoomTravel':
      return (
        <Row>
          <Num label="Start Scale" value={m.startScale} fallback={0.5} min={0} step={0.1} onChange={(v) => update('startScale', v || 0)} />
          <Num label="End Scale" value={m.endScale} fallback={1.5} min={0} step={0.1} onChange={(v) => update('endScale', v || 0)} />
        </Row>
      )
    case 'orbitMotion':
    case 'circularMotion':
    case 'orbit3D':
      return (
        <>
          <Row>
            <Num label="Radius" value={m.radius} fallback={120} min={0} step={10} onChange={(v) => update('radius', v || 0)} />
            <Num label="Start °" value={m.startAngle} fallback={0} step={15} onChange={(v) => update('startAngle', v || 0)} />
          </Row>
          <Num label="End °" value={m.endAngle} fallback={360} step={15} onChange={(v) => update('endAngle', v || 0)} />
        </>
      )

    // --- Category 3 ---
    case 'overshootSettle':
      return <Num label="Overshoot" value={m.overshoot} fallback={0.2} min={0} max={1} step={0.05} onChange={(v) => update('overshoot', v)} />
    case 'elasticMotion':
      return (
        <Row>
          <Num label="Amplitude" value={m.amplitude} fallback={0.3} min={0} step={0.05} onChange={(v) => update('amplitude', v)} />
          <Num label="Frequency" value={m.frequency} fallback={3} min={0} step={0.5} onChange={(v) => update('frequency', v)} />
        </Row>
      )
    case 'staggeredCharMotion':
    case 'staggeredWordMotion':
      return (
        <Row>
          <Num label="Offset Y" value={m.offsetY} fallback={40} step={5} onChange={(v) => update('offsetY', v || 0)} />
          <Num label="Stagger" value={m.stagger} fallback={0.12} min={0} step={0.01} onChange={(v) => update('stagger', v)} />
        </Row>
      )
    case 'waveMotion':
      return (
        <Row>
          <Num label="Amplitude" value={m.amplitude} fallback={12} step={1} onChange={(v) => update('amplitude', v || 0)} />
          <Num label="Cycles" value={m.cycles} fallback={2} min={0} step={0.5} onChange={(v) => update('cycles', v || 0)} />
        </Row>
      )
    case 'scatterConverge':
      return <Num label="Distance" value={m.distance} fallback={150} step={10} onChange={(v) => update('distance', v || 0)} />
    case 'trackingExpandCompress':
      return (
        <Row>
          <Num label="Start Tracking" value={m.startTracking} fallback={0} step={1} onChange={(v) => update('startTracking', v || 0)} />
          <Num label="End Tracking" value={m.endTracking} fallback={30} step={1} onChange={(v) => update('endTracking', v || 0)} />
        </Row>
      )

    // --- Category 4 ---
    case 'rotation3D':
      return (
        <>
          <Row>
            <Num label="End X °" value={m.endX} fallback={0} step={15} onChange={(v) => update('endX', v || 0)} />
            <Num label="End Y °" value={m.endY} fallback={360} step={15} onChange={(v) => update('endY', v || 0)} />
          </Row>
          <Num label="End Z °" value={m.endZ} fallback={0} step={15} onChange={(v) => update('endZ', v || 0)} />
        </>
      )
    case 'perspectiveTilt':
      return (
        <Row>
          <Num label="Horizontal Tilt °" value={m.horizontalTilt} fallback={0} step={5} onChange={(v) => update('horizontalTilt', v || 0)} />
          <Num label="Vertical Tilt °" value={m.verticalTilt} fallback={30} step={5} onChange={(v) => update('verticalTilt', v || 0)} />
        </Row>
      )
    case 'depthTravel':
      return (
        <Row>
          <Num label="Start Z" value={m.startZ} fallback={1200} step={50} onChange={(v) => update('startZ', v || 0)} />
          <Num label="End Z" value={m.endZ} fallback={0} step={50} onChange={(v) => update('endZ', v || 0)} />
        </Row>
      )
    case 'parallaxMotion':
      return (
        <Row>
          <Num label="Camera X" value={m.cameraX} fallback={200} step={10} onChange={(v) => update('cameraX', v || 0)} />
          <Num label="Depth" value={m.depth} fallback={1} min={0.1} step={0.1} onChange={(v) => update('depth', v)} />
        </Row>
      )
    case 'flipTumble3D':
      return (
        <Row>
          <Num label="Turns X" value={m.turnsX} fallback={0} min={0} step={1} onChange={(v) => update('turnsX', Math.floor(v || 0))} />
          <Num label="Turns Y" value={m.turnsY} fallback={1} min={0} step={1} onChange={(v) => update('turnsY', Math.floor(v || 0))} />
        </Row>
      )

    // --- Category 5 ---
    case 'spiralMotion':
      return (
        <>
          <Row>
            <Num label="Start Radius" value={m.startRadius} fallback={0} min={0} step={10} onChange={(v) => update('startRadius', v || 0)} />
            <Num label="End Radius" value={m.endRadius} fallback={150} min={0} step={10} onChange={(v) => update('endRadius', v || 0)} />
          </Row>
          <Num label="Turns" value={m.turns} fallback={2} min={0} step={0.5} onChange={(v) => update('turns', v || 0)} />
        </>
      )
    case 'wavePathMotion':
      return (
        <>
          <Row>
            <Num label="Distance" value={m.distance} fallback={600} step={10} onChange={(v) => update('distance', v || 0)} />
            <Num label="Amplitude" value={m.amplitude} fallback={40} step={5} onChange={(v) => update('amplitude', v || 0)} />
          </Row>
          <Num label="Waves" value={m.waves} fallback={2} min={0} step={0.5} onChange={(v) => update('waves', v || 0)} />
        </>
      )
    case 'textOnPath':
    case 'pathMorph':
    case 'layoutReflow':
    case 'followPath':
    case 'motionPathWithStops':
    case 'keyframeInterpolation':
    case 'speedGraph':
    case 'perCharacterControls':
    case 'presets':
      return (
        <p className="text-xs text-gray-500 leading-snug">
          This motion uses a path / keyframe / per-character configuration.
          Edit its points in the timeline, or adjust timing, easing and the
          modifiers below.
        </p>
      )

    // --- Category 6 ---
    case 'floatingDrifting':
      return (
        <Row>
          <Num label="Amplitude X" value={m.amplitudeX} fallback={12} step={1} onChange={(v) => update('amplitudeX', v || 0)} />
          <Num label="Amplitude Y" value={m.amplitudeY} fallback={18} step={1} onChange={(v) => update('amplitudeY', v || 0)} />
        </Row>
      )
    case 'springMotion':
      return (
        <>
          <Row>
            <Num label="Stiffness" value={m.stiffness} fallback={120} min={1} step={10} onChange={(v) => update('stiffness', v)} />
            <Num label="Damping" value={m.damping} fallback={12} min={0} step={1} onChange={(v) => update('damping', v)} />
          </Row>
          <Row>
            <Num label="Target X" value={m.targetX} fallback={100} step={10} onChange={(v) => update('targetX', v || 0)} />
            <Num label="Target Y" value={m.targetY} fallback={0} step={10} onChange={(v) => update('targetY', v || 0)} />
          </Row>
        </>
      )
    case 'pendulumSwing':
      return (
        <Row>
          <Num label="Angle °" value={m.angle} fallback={20} step={5} onChange={(v) => update('angle', v || 0)} />
          <Num label="Frequency" value={m.frequency} fallback={0.8} min={0} step={0.1} onChange={(v) => update('frequency', v)} />
        </Row>
      )
    case 'jitterTremble':
      return (
        <Row>
          <Num label="Position Amp" value={m.positionAmplitude} fallback={3} min={0} step={0.5} onChange={(v) => update('positionAmplitude', v)} />
          <Num label="Rotation Amp" value={m.rotationAmplitude} fallback={2} min={0} step={0.5} onChange={(v) => update('rotationAmplitude', v)} />
        </Row>
      )
    case 'impulseMotion':
      return (
        <>
          <Num label="Trigger Time (s)" value={m.triggerTime} fallback={0} min={0} step={0.1} onChange={(v) => update('triggerTime', Math.max(0, v || 0))} />
          <Row>
            <Num label="Strength X" value={m.strengthX} fallback={0} step={5} onChange={(v) => update('strengthX', v || 0)} />
            <Num label="Strength Y" value={m.strengthY} fallback={-40} step={5} onChange={(v) => update('strengthY', v || 0)} />
          </Row>
        </>
      )
    case 'rhythmicMotion':
      return <Num label="Pulse Scale" value={m.pulseScale} fallback={0.15} min={0} step={0.05} onChange={(v) => update('pulseScale', v)} />

    // --- Category 7 (editable scalars) ---
    case 'motionBlur':
      return (
        <>
          <Row>
            <Num label="Amount" value={m.amount} fallback={8} min={0} step={1} onChange={(v) => update('amount', v || 0)} />
            <Num label="Angle °" value={m.angle} fallback={0} step={15} onChange={(v) => update('angle', v || 0)} />
          </Row>
          <Check label="Isotropic (blur all directions)" value={m.isotropic} fallback={false} onChange={(v) => update('isotropic', v)} />
        </>
      )
    case 'loopControls':
      return (
        <Row>
          <Num label="End X" value={m.endX} fallback={0} step={10} onChange={(v) => update('endX', v || 0)} />
          <Num label="End Y" value={m.endY} fallback={-60} step={10} onChange={(v) => update('endY', v || 0)} />
        </Row>
      )
    case 'constraints':
      return (
        <Row>
          <Num label="End X" value={m.endX} fallback={400} step={10} onChange={(v) => update('endX', v || 0)} />
          <Num label="End Y" value={m.endY} fallback={0} step={10} onChange={(v) => update('endY', v || 0)} />
        </Row>
      )

    default:
      return null
  }
}

export default MotionPropertiesPanel
